import { useState, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill, CopyButton } from '@ui-kit';
import { useModuleCatalog } from '../hooks/useModuleCatalog';
import { useWatches } from '../hooks/useWatches';
import { ModuleCatalogTree } from '../components/ModuleCatalogTree';
import { ModuleFacetBar } from '../components/ModuleFacetBar';
import { WatchToggle } from '../components/WatchToggle';
import { matchCategory, matchStatus, matchDemo, matchOptin, matchBiz, matchWatch, matchQuery } from '../data/facet-predicates';
import takeThisJson from '../data/take-this.json';
import { ValidatePanel } from '../components/ValidatePanel';
import { MaskPanel } from '../components/MaskPanel';
import { ExcelPanel } from '../components/ExcelPanel';
import { EventPanel } from '../components/EventPanel';
import { ErrorPanel } from '../components/ErrorPanel';
import { FilePanel } from '../components/FilePanel';
import { CachePanel } from '../components/CachePanel';
import { UtilityPanel } from '../components/UtilityPanel';
import { CryptoPanel } from '../components/CryptoPanel';
import { JpaPanel } from '../components/JpaPanel';
import { RouteMatchPanel } from '../components/RouteMatchPanel';
import { AuthCheckPanel } from '../components/AuthCheckPanel';
import { RateLimitPanel } from '../components/RateLimitPanel';
import { HealthCheckPanel } from '../components/HealthCheckPanel';
import { KafkaDemoPanel } from '../components/KafkaDemoPanel';
import { RabbitDemoPanel } from '../components/RabbitDemoPanel';
import { ElasticsearchDemoPanel } from '../components/ElasticsearchDemoPanel';
import { MongoDemoPanel } from '../components/MongoDemoPanel';
import { OtelStatusPanel } from '../components/OtelStatusPanel';
import { OutboxDemoPanel } from '../components/OutboxDemoPanel';
import { OAuth2DemoPanel } from '../components/OAuth2DemoPanel';
import { StateMachineDemoPanel } from '../components/demos/StateMachineDemoPanel';
import { SagaDemoPanel } from '../components/demos/SagaDemoPanel';
import { ReportDemoPanel } from '../components/demos/ReportDemoPanel';
import { RetentionPreviewDemoPanel } from '../components/demos/RetentionPreviewDemoPanel';
import { ConfigLookupDemoPanel } from '../components/demos/ConfigLookupDemoPanel';
import { BarcodeDemoPanel } from '../components/demos/BarcodeDemoPanel';
import { TemplateRenderDemoPanel } from '../components/demos/TemplateRenderDemoPanel';
import { PdfDemoPanel } from '../components/demos/PdfDemoPanel';
import { ImageResizeDemoPanel } from '../components/demos/ImageResizeDemoPanel';
import { FeatureToggleDemoPanel } from '../components/demos/FeatureToggleDemoPanel';
import { LearningHints } from '../components/LearningHints';
import { ModuleDocsModal } from '../components/ModuleDocsModal';
import { TakeThisPanel } from '../components/TakeThisPanel';
import { ConfigSection } from '../components/ConfigSection';
import { CompatibilityMatrix } from '../components/CompatibilityMatrix';
import { MaturityBadge } from '../components/MaturityBadge';
import { DownloadStatBadge } from '../components/DownloadStatBadge';
import { SUPERSEDE_JUMP_DISABLED_CODES } from '../data/maturity';
import { TryItOutPanel } from '../components/TryItOutPanel';
import { hasTryItOut } from '../data/safe-get-operations';
import { OpenApiExportLinks } from '../components/OpenApiExportLinks';
import { useModuleDescriptions } from '../hooks/useModuleDescriptions';
import { RELATED_DEMO_ROUTE } from '../data/related-demo-routes';
import { RecentChangesPanel } from '../components/RecentChangesPanel';
// queue #664 §UAT-2026-07-09-R664 §R664-3: '레시피' 뷰 — 갤러리/상세 + 정적 조합 데이터
import { RECIPES } from '../data/recipes';
import { RecipeGallery } from '../components/RecipeGallery';
import { RecipeDetailPanel } from '../components/RecipeDetailPanel';
// queue #666 §UAT-2026-07-09-R666 §R666-3/§R666-4: 알려진 함정·트러블슈팅 KB —
// '함정 사전' 뷰(갤러리/상세) + 모듈 상세 '트러블슈팅' 섹션. 정적 큐레이션 데이터(BE 0).
import { PITFALLS } from '../data/pitfalls';
import { PitfallDictionary } from '../components/PitfallDictionary';
import { PitfallDetailPanel } from '../components/PitfallDetailPanel';
import { TroubleshootingSection } from '../components/TroubleshootingSection';
// queue #667 §UAT-2026-07-09-R667 §R667-3/§R667-4: 유사 능력 선택 가이드(Decision Guide) —
// '선택 가이드' 뷰(군 목록/비교표) + 모듈 상세 '대안과 비교' 진입점. 정적 큐레이션 데이터(BE 0).
import { DECISION_GUIDES } from '../data/decision-guides';
import { DecisionGuideView } from '../components/DecisionGuideView';
import { AlternativesLink } from '../components/AlternativesLink';
import { ChangeHistorySection } from '../components/ChangeHistorySection';
import { UpgradeGuideSection } from '../components/UpgradeGuideSection';
import moduleMaturityJson from '../data/module-maturity.json';
import type { ModuleNode, DemoPanel, TakeThisData, ModuleMaturity } from '../types/showcase';
// queue #663 §UAT-2026-07-09-R663 §R663-3: 모듈 피드백/문의 채널 — 다이얼로그 + admin 링크
import { FeedbackDialog } from '../components/FeedbackDialog';
import { useHasRole } from '../../_shared/hooks/useCurrentUser';

// queue #640 §R640-2④: opt-in 패싯은 빌드타임 take-this.json[code].yml.alwaysOn 파생 —
// TakeThisPanel.tsx 가 이미 static import 중인 동일 생성물을 페이지도 재사용(SSOT 1개).
const TAKE_THIS_DATA = takeThisJson as unknown as TakeThisData;
// queue #658 §R658-2: 전역 변경 피드 — §R643 산출물 read-only 재사용(신규 데이터 원천 0).
const MODULE_MATURITY = moduleMaturityJson as unknown as ModuleMaturity;

/** Map from module code to LearningHints moduleKey */
function toHintKey(code: string): string {
  return code.toLowerCase();
}

/**
 * queue #679 §UAT-2026-07-13-R679 §R679-2 — 노드 선택 시 아웃바운드 해시 동기화.
 * 노드-코드 해시만 write. view=/q=/패싯 쿼리(location.search)와 pathname 을 보존해 합성.
 * pushState 아님 → 뒤로가기 히스토리 오염 없음(replace).
 * 루프 가드: 이미 동일 해시면 write 스킵(가드 A · §R679-4).
 */
function syncNodeHash(code: string) {
  const current = window.location.hash.replace('#', '');
  if (current === code) return; // 가드 A: 동일 해시 재기록 방지
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, '', `${pathname}${search}#${code}`);
}

/** Keyframe CSS — injected once */
const KEYFRAME_CSS = `
@keyframes spin  { to { transform: rotate(360deg); } }
@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
`;

// ─── Skeleton for catalog loading ────────────────────────────────────────────

function CatalogSkeleton() {
  const { t } = useTranslation();
  return (
    <div aria-busy="true" aria-label={t('showcase.panel.loading')}>
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 32,
            margin: 'var(--spacing-1) var(--spacing-3)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-muted)',
            animation: 'pulse 1.5s ease-in-out infinite',
            opacity: 1 - i * 0.06,
          }}
        />
      ))}
    </div>
  );
}

// ─── Supersede notice (#617) ──────────────────────────────────────────────────
// BE ModuleNode 는 additive 필드 supersededBy?/supersedes? 를 반환한다 (null 은 JSON 상
// 생략될 수 있어 optional 로 취급 — `!= null` 로 null/undefined 를 함께 걸러낸다, `!== null`
// 강비교 금지). supersededBy 가 "gateway-self" 이면 개별 후계 코드가 아니라 gateway 자체
// (G001~G060 체계)로 흡수된 것이므로 이동 버튼 대신 안내 텍스트만 표시한다.
//
// #617 spec-delta §R617: C032 는 예외 — common-capabilities.md 의 후계 코드 "G015"(신규
// ApiKeyAuthenticationGlobalFilter 로드맵 항목) 가, 쇼케이스 자체 G-넘버링 체계(G001~G015,
// R12 라이브 데모)의 기존 G015(WebSocket) 와 코드값이 충돌한다. 두 체계가 서로 다른 실체를
// 같은 "G015" 문자열로 가리키므로 이동 버튼을 누르면 엉뚱한 노드(WebSocket)로 이동하는
// 오류가 발생한다 — C032 만 배지/라벨 텍스트로만 표시하고 이동 버튼은 렌더하지 않는다.
// queue #643 §R643-6: SUPERSEDE_JUMP_DISABLED_CODES 는 data/maturity.ts 로 이동(단일 진실
// 원천) — MaturityBadge 와 공유하기 위해 순환 import 없는 데이터 모듈에 둔다.

function SupersedeNotice({ node, onJumpToCode }: { node: ModuleNode; onJumpToCode?: (code: string) => void }) {
  const { t } = useTranslation();
  const successor = node.supersededBy;
  const predecessors = node.supersedes;
  const hasSuccessor = successor != null;
  const hasPredecessors = !!predecessors && predecessors.length > 0;
  if (!hasSuccessor && !hasPredecessors) return null;

  const jumpDisabled = SUPERSEDE_JUMP_DISABLED_CODES.has(node.code);

  return (
    <div style={{ marginBottom: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {hasSuccessor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <StatusPill tone="warning">{t('showcase.supersede.badge')}</StatusPill>
          {successor === 'gateway-self' ? (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {t('showcase.supersede.gatewaySelfNotice')}
            </span>
          ) : jumpDisabled ? (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              {t('showcase.supersede.successorLabelOnly', { code: successor })}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onJumpToCode?.(successor as string)}
              style={{
                background: 'none',
                border: '1px solid var(--color-brand-primary)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-brand-primary)',
                cursor: 'pointer',
              }}
            >
              {t('showcase.supersede.successorLink', { code: successor })}
            </button>
          )}
        </div>
      )}
      {hasPredecessors && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <StatusPill tone="info">
            {t('showcase.supersede.supersedesBadge', { count: predecessors!.length })}
          </StatusPill>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
            {predecessors!.join(', ')}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Reusable module detail body (metadata + 상세 설명 + public API + docs) ─────
// UAT 2026-06-01 #466: InfoPanel(비-데모) 과 데모 패널 하단이 동일한 상세 안내 구조를
// 공유하도록 본문을 별도 컴포넌트로 추출. 데이터 소스(MODULE_DESCRIPTIONS · publicApis ·
// ModuleDocsModal)는 그대로 재사용한다 — 신규 구현 없음.
// #617: SUPERSEDED 배지/이동 버튼도 이 공유 본문에 추가해 InfoPanel·데모 패널 양쪽에 일괄 반영.

function ModuleDetailBody({
  node,
  onJumpToCode,
  onSelectPitfall,
  onNavigateToAlternatives,
  autoOpenSpecSlug,
  onAutoOpenSpecConsumed,
}: {
  node: ModuleNode;
  onJumpToCode?: (code: string) => void;
  /** queue #666 §R666-3: 트러블슈팅 카드 클릭 시 — 전역 함정 사전으로 전환 + 해당 함정 선택 */
  onSelectPitfall?: (id: string) => void;
  /** queue #667 §R667-3: '대안과 비교' 카드 클릭 시 — 전역 선택 가이드 뷰로 전환 + 해당 군/코드 하이라이트 */
  onNavigateToAlternatives?: (guideId: string, code: string) => void;
  /** queue #693 §R693-5: `?spec=<slug>` 딥링크 — 이 노드의 ModuleDocsModal 을 자동 오픈 + 앵커 스크롤 */
  autoOpenSpecSlug?: string | null;
  /** queue #693 §R693-5: autoOpenSpecSlug 로 열린 모달이 닫힐 때 상위(`?spec=` 제거)에 위임 */
  onAutoOpenSpecConsumed?: () => void;
}) {
  const { t } = useTranslation();
  // R11-#293/#294: per-module detailed description from module-descriptions.{ko,en}.tsx
  // queue #656 §R656-2: locale-aware, lazily-loaded via useModuleDescriptions() —
  // returns {} for a short tick on first load/locale switch, which correctly falls
  // through to the existing LearningHints fallback below (no separate loading UI).
  const moduleDescriptions = useModuleDescriptions();
  const moduleDescription = moduleDescriptions[node.code];

  // queue #645 §R645-4: publicApis 배지 클릭화 — 안전 GET 화이트리스트 매핑이 있는 노드만
  // try-it-out 드로어를 연다(없으면 배지는 현행 정적 표시 유지).
  const [tryItOutOpen, setTryItOutOpen] = useState(false);
  const tryItOutAvailable = hasTryItOut(node.code);

  // queue #663 §UAT-2026-07-09-R663 §R663-3: 피드백/문의 다이얼로그 — moduleCode 자동 프리필
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackToast, setFeedbackToast] = useState<{ text: string; tone: 'success' | 'error' } | null>(null);
  const showFeedbackToast = (text: string, tone: 'success' | 'error') => {
    setFeedbackToast({ text, tone });
    setTimeout(() => setFeedbackToast(null), 4000);
  };

  return (
    <>
      <SupersedeNotice node={node} onJumpToCode={onJumpToCode} />

      {/* queue #643 §R643-5: 성숙도 등급 배지(GA/Beta/Deprecated) — SupersedeNotice 직후·version <dl> 직전 */}
      <MaturityBadge node={node} />

      {/* queue #662 §R662-3: 다운로드 수 + '인기' 배지 — MaturityBadge 직후·version <dl> 직전 */}
      <DownloadStatBadge node={node} />

      <dl style={{ marginBottom: 'var(--spacing-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
          <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>artifact</dt>
          <dd style={{ margin: 0 }}>
            <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>
              {node.artifact}
            </code>
          </dd>
          <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>version</dt>
          <dd style={{ margin: 0 }}>
            <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
              {node.version || t('showcase.compat.runtimeAbsent')}
            </code>
          </dd>
          <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>since</dt>
          <dd style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>
            {node.since}
          </dd>
        </div>
      </dl>

      {/* queue #637 §R637-4: 호환성 매트릭스 — Spring Boot/JDK/eraf-bom/eraf-parent/모듈 버전 + /getting-started 딥링크 */}
      <CompatibilityMatrix node={node} />

      {/* R11-#293/#294: detailed module description (per-module override) */}
      {moduleDescription && (
        <div data-testid="module-description-block" style={{ marginBottom: 'var(--spacing-4)' }}>
          {moduleDescription}
        </div>
      )}

      {node.publicApis.length > 0 && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', margin: 0 }}>
              {t('showcase.panel.publicApis')}
            </p>
            {/* queue #668 §R668-2: OpenAPI JSON 다운로드 + swagger group 딥링크(§R645 인프라 재사용) —
                기존 인라인 swagger 딥링크(§R645)를 이 컴포넌트로 이관·확장. 내부에서 가용성 게이트 자체 처리. */}
            <OpenApiExportLinks />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
            {node.publicApis.map((api) =>
              tryItOutAvailable ? (
                <button
                  key={api}
                  type="button"
                  onClick={() => setTryItOutOpen(true)}
                  title={t('showcase.tryit.badgeHint')}
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-brand-primary)',
                    padding: '2px var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-brand-primary)',
                    cursor: 'pointer',
                  }}
                >
                  {api}
                </button>
              ) : (
                <code
                  key={api}
                  style={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 'var(--font-size-xs)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    padding: '2px var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-brand-primary)',
                  }}
                >
                  {api}
                </code>
              )
            )}
          </div>
        </div>
      )}

      {/* queue #645 §R645-4: API 탐색(try-it-out) 드로어 — 안전 GET 화이트리스트 매핑 노드만 */}
      {tryItOutAvailable && (
        <TryItOutPanel node={node} open={tryItOutOpen} onClose={() => setTryItOutOpen(false)} />
      )}

      {/* queue #665 §UAT-2026-07-09-R665 §R665-3: 설정 속성 카탈로그(Config Reference) 섹션 —
          TakeThisPanel 직전. 컴포넌트 내부에서 속성 0건이면 자체적으로 null 반환(가드). */}
      <ConfigSection node={node} />

      {/* queue #666 §UAT-2026-07-09-R666 §R666-3: 트러블슈팅 섹션 — ConfigSection 직후.
          컴포넌트 내부에서 매핑 함정 0건이면 자체적으로 null 반환(가드 · §R665 계보). */}
      <TroubleshootingSection node={node} onSelectPitfall={onSelectPitfall} />

      {/* queue #667 §UAT-2026-07-09-R667 §R667-3: '대안과 비교' 진입점 — TroubleshootingSection
          직후. 컴포넌트 내부에서 매핑 비교군 0건이면 자체적으로 null 반환(가드 · §R665/§R666 계보). */}
      <AlternativesLink node={node} onNavigate={onNavigateToAlternatives} />

      {/* queue #692 §UAT-2026-07-14-R692 §R692-3: '업그레이드 시 확인사항' 섹션 — AlternativesLink
          직후·ChangeHistorySection 직전. 컴포넌트 내부에서 해당 artifact 이력 0건/미가용이면
          자체적으로 null 반환(가드 · §R665/§R666/§R667/§R672 계보). */}
      <UpgradeGuideSection node={node} />

      {/* queue #672 §UAT-2026-07-09-R672 §R672-4: 모듈별 '변경 이력' 섹션 — AlternativesLink
          직후·TakeThisPanel 직전. 컴포넌트 내부에서 해당 artifact 이력 0건/미가용이면 자체적으로
          null 반환(가드 · §R665/§R666/§R667 계보). */}
      <ChangeHistorySection node={node} />

      {/* queue #630 §R630-0: 가져다쓰기(Take-this) 패널 — ModuleDocsModal 직전 삽입.
          컴포넌트 내부에서 SUPERSEDED 노드는 자체적으로 null 반환(가드). */}
      <TakeThisPanel node={node} onJumpToCode={onJumpToCode} />

      {/* queue #663 §UAT-2026-07-09-R663 §R663-3: 피드백/문의 버튼 — TakeThisPanel 이후·ModuleDocsModal 직전.
          guest/user/admin 전원 제출 가능(인증-any) — moduleCode 는 현재 상세 노드로 자동 프리필. */}
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          data-testid="showcase-feedback-open-btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-2) var(--spacing-4)',
            border: '1px solid var(--color-brand-primary)',
            borderRadius: 'var(--radius-md)',
            background: 'transparent',
            color: 'var(--color-brand-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('showcase.feedback.button')}
        </button>
      </div>

      <FeedbackDialog
        open={feedbackOpen}
        moduleCode={node.code}
        onClose={() => setFeedbackOpen(false)}
        onSuccess={(msg) => showFeedbackToast(msg, 'success')}
        onError={(msg) => showFeedbackToast(msg, 'error')}
      />

      {feedbackToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            background: feedbackToast.tone === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {feedbackToast.text}
        </div>
      )}

      {/* R11-#287: Replace broken github link with ModuleDocsModal */}
      {/* #688 §R688-6: onJumpToCode — SUPERSEDED 노드의 "대체 능력 스펙 보기" 클릭 시 트리 노드 전환 */}
      {/* #693 §R693-5: key=node.code — 노드 전환 시 재마운트해 autoOpenSpecSlug 마운트 효과가
          이전 노드의 오픈 상태를 이어받지 않도록 한다 */}
      <ModuleDocsModal
        key={node.code}
        node={node}
        onJumpToCode={onJumpToCode}
        autoOpenSpecSlug={autoOpenSpecSlug}
        onAutoOpenConsumed={onAutoOpenSpecConsumed}
      />
    </>
  );
}

// ─── Related biz shortcut (queue #612 · §R612-1) ─────────────────────────────
// 5 능력(C036/C061/C062/C074/C075)은 owner biz 에 이미 전용 인터랙티브 콘솔이 시연 완료되어
// 있으므로, 쇼케이스 내부에 데모 패널을 신규로 만들지 않고 "연관 biz 바로가기" 딥링크만 제공한다.
// demoEndpoint 는 null 유지 — 링크는 데모 패널이 아니다 (신규 BE/패널 0).

function RelatedBizShortcut({ node }: { node: ModuleNode }) {
  const { t } = useTranslation();
  const related = RELATED_DEMO_ROUTE[node.code];
  if (!related) return null;

  return (
    <div style={{ marginTop: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
        {t('showcase.related.title')}
      </p>
      <Link
        to={related.route}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          border: '1px solid var(--color-brand-primary)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-brand-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        {t(related.i18nKey)}
        <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
}

// ─── Info panel for modules without a demo ───────────────────────────────────

function InfoPanel({
  node,
  onJumpToCode,
  onSelectPitfall,
  onNavigateToAlternatives,
  autoOpenSpecSlug,
  onAutoOpenSpecConsumed,
}: {
  node: ModuleNode;
  onJumpToCode?: (code: string) => void;
  onSelectPitfall?: (id: string) => void;
  onNavigateToAlternatives?: (guideId: string, code: string) => void;
  autoOpenSpecSlug?: string | null;
  onAutoOpenSpecConsumed?: () => void;
}) {
  const { t, i18n } = useTranslation();
  // UAT 2026-05-20 #459: EN 토글 시 제목/부제 라벨 어순 전환 (EN 우선, i18n 미초기화 시 ko)
  const isEn = (i18n?.language ?? 'ko').toLowerCase().startsWith('en');
  const primaryName = isEn && node.nameEn?.trim() ? node.nameEn : node.nameKo;
  const secondaryName = isEn && node.nameEn?.trim() ? node.nameKo : node.nameEn;

  return (
    <section aria-labelledby="info-panel-title">
      <h2
        id="info-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {node.code} — {primaryName}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-4)' }}>
        {secondaryName}
      </p>

      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', fontStyle: 'italic', marginBottom: 'var(--spacing-4)' }}>
        {t('showcase.panel.noDemo')}
      </p>

      {/* UAT 2026-06-01 #466: 공통 상세 본문 (데모 패널과 동일 컴포넌트 재사용) */}
      <ModuleDetailBody
        node={node}
        onJumpToCode={onJumpToCode}
        onSelectPitfall={onSelectPitfall}
        onNavigateToAlternatives={onNavigateToAlternatives}
        autoOpenSpecSlug={autoOpenSpecSlug}
        onAutoOpenSpecConsumed={onAutoOpenSpecConsumed}
      />

      {/* queue #612 §R612-1: 기시연 완료 능력 → owner biz 데모 화면 딥링크 (해당 노드만 렌더) */}
      <RelatedBizShortcut node={node} />

      {/* R7-021: 3 learning hint cards */}
      <LearningHints moduleKey={toHintKey(node.code)} />
    </section>
  );
}

// ─── Biz cross-link InfoCards ─────────────────────────────────────────────────

interface BizLink {
  moduleCode: string;
  moduleLabel: string;
  bizPath: string;
  bizLabel: string;
  description: string;
}

function BizCrossLinkCards() {
  const { t } = useTranslation();

  const BIZ_LINKS: BizLink[] = [
    {
      moduleCode: 'C039',
      moduleLabel: 'AuditableEntity',
      bizPath: '/audit-logs',
      bizLabel: t('showcase.crossLink.auditLogs'),
      description: t('showcase.crossLink.auditLogsDesc'),
    },
    {
      moduleCode: 'C028~C030',
      moduleLabel: 'RBAC',
      bizPath: '/admin/roles',
      bizLabel: t('showcase.crossLink.roles'),
      description: t('showcase.crossLink.rolesDesc'),
    },
    {
      moduleCode: 'C009',
      moduleLabel: 'FileManager',
      bizPath: '/attachments',
      bizLabel: t('showcase.crossLink.attachments'),
      description: t('showcase.crossLink.attachmentsDesc'),
    },
    {
      moduleCode: 'C008',
      moduleLabel: 'EventPublisher',
      bizPath: '/event-monitor',
      bizLabel: t('showcase.crossLink.eventMonitor'),
      description: t('showcase.crossLink.eventMonitorDesc'),
    },
  ];

  return (
    <div style={{ marginTop: 'var(--spacing-6)' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-3)' }}>
        {t('showcase.crossLink.title')}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--spacing-3)' }}>
        {BIZ_LINKS.map((link) => (
          <a
            key={link.moduleCode}
            href={link.bizPath}
            style={{
              display: 'block',
              padding: 'var(--spacing-3) var(--spacing-4)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-elevated)',
              textDecoration: 'none',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--color-brand-primary)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--color-surface-border)'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-1)' }}>
              <code style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)', fontFamily: 'var(--font-family-mono)' }}>
                {link.moduleCode}
              </code>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>→ {link.bizLabel}</span>
            </div>
            <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              {link.description}
            </p>
          </a>
        ))}
      </div>
    </div>
  );
}

// ─── Common modules usage panel ──────────────────────────────────────────────

interface CommonModule {
  module: string;
  api: string;
  roleKey: string;
  snippet: string;
}

function CommonUsagePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const COMMON_MODULES_DATA: CommonModule[] = [
    { module: 'eraf-core C001', api: 'BusinessException + ErrorCode + GlobalExceptionHandler', roleKey: 'businessException', snippet: 'throw new BusinessException(CommonErrorCode.NOT_FOUND, "42");' },
    { module: 'eraf-core C002', api: 'ApiResponse.success/empty/failure(...)', roleKey: 'apiResponse', snippet: 'return ApiResponse.success(service.mask(req));' },
    { module: 'eraf-core C003', api: 'ValidationFacade.validate(target) + @NoXss/@Email/...', roleKey: 'validationFacade', snippet: 'ValidationFacade.validate(new XssTrialDto(value));' },
    { module: 'eraf-core C006', api: 'RequestContextHolder.current()', roleKey: 'requestContext', snippet: 'var ctx = RequestContextHolder.current(); ctx.userId();' },
    { module: 'eraf-core C008', api: 'EventPublisher.publishImmediate(event)', roleKey: 'eventPublisher', snippet: 'events.publishImmediate(new ShowcaseDemoEvent(payload, userId));' },
    { module: 'eraf-core C009', api: 'FileService.upload(multipart, meta)', roleKey: 'fileService', snippet: 'fileService.upload(multipart, FileMeta.builder().category("showcase-trial").build());' },
    { module: 'eraf-core C013', api: 'Masker.mask(value, type, mode)', roleKey: 'masker', snippet: 'masker.mask("user@example.com", MaskType.EMAIL, MaskingMode.DEFAULT);' },
    { module: 'eraf-core C014', api: '@Audited(action=CUSTOM, customAction="SHOWCASE_*")', roleKey: 'audited', snippet: '@Audited(action = AuditAction.CUSTOM, customAction = "SHOWCASE_EVENT_PUBLISHED")' },
    { module: 'eraf-security C028', api: 'JwtAuthenticationFilter', roleKey: 'jwtFilter', snippet: t('showcase.commonUsage.modules.snippetFilter') },
    { module: 'eraf-security C029', api: 'PathPolicy.authenticated("/api/eraf/showcase/**")', roleKey: 'pathPolicy', snippet: 'PathPolicy.authenticated("/api/eraf/showcase/**")' },
    { module: 'eraf-excel C078', api: 'ExcelExporter.write(out, rows, opts)', roleKey: 'excelExporter', snippet: 'excelExporter.write(resp.getOutputStream(), rows, ExcelExportOptions.defaults());' },
    { module: 'Spring Cache', api: '@Cacheable("showcase-demo")', roleKey: 'springCache', snippet: '@Cacheable("showcase-demo") public String compute(String key) { Thread.sleep(1500); ... }' },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-8)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="common-usage-panel"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-elevated)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span>{t('showcase.commonUsage.title')}</span>
        <span aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {/* queue #638 §R638-6: 페이지-자기공시(이 표) vs 노드-레벨 ⑥블록(가져다쓰기)의 목적 구분 —
          두 표는 답하는 질문이 달라 둘 다 유지(중복 아님). 소비자 혼동 방지용 안내 1줄. */}
      <p
        style={{
          margin: 0,
          padding: 'var(--spacing-2) var(--spacing-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          background: 'var(--color-surface-background)',
          borderBottom: open ? '1px solid var(--color-surface-border)' : 'none',
        }}
      >
        {t('showcase.commonUsage.scopeNote')}
      </p>

      {open && (
        <div id="common-usage-panel" style={{ overflowX: 'auto' }}>
          <table
            aria-label={t('showcase.commonUsage.ariaLabel')}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-background)',
            }}
          >
            <thead>
              <tr>
                {[
                  t('showcase.commonUsage.col.module'),
                  t('showcase.commonUsage.col.api'),
                  t('showcase.commonUsage.col.role'),
                  t('showcase.commonUsage.col.snippet'),
                ].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      textAlign: 'left',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontWeight: 700,
                      background: 'var(--color-surface-muted)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMMON_MODULES_DATA.map((m, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES_DATA.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES_DATA.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        color: 'var(--color-brand-primary)',
                        fontSize: 'var(--font-size-xs)',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.api}
                    </code>
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES_DATA.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {t(`showcase.commonUsage.modules.${m.roleKey}`)}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: idx < COMMON_MODULES_DATA.length - 1 ? '1px solid var(--color-surface-border)' : 'none',
                    }}
                  >
                    <code
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        background: 'var(--color-surface-elevated)',
                        padding: '2px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'inline-block',
                        wordBreak: 'break-all',
                      }}
                    >
                      {m.snippet}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Demo panel resolution (shared by auto-select-by-hash + supersede jump-to-code) ──
// Derive panel from demoEndpoint (same logic as ModuleCatalogTree's getDemoPanel).

function resolveDemoPanel(n: ModuleNode): DemoPanel {
  if (n.code === 'C009') return 'info';
  if (!n.demoEndpoint) return 'info';
  if (n.demoEndpoint.includes('/validate')) return 'validate';
  if (n.demoEndpoint.includes('/mask')) return 'mask';
  if (n.demoEndpoint.includes('/excel')) return 'excel';
  if (n.demoEndpoint.includes('/event')) return 'event';
  if (n.demoEndpoint.includes('/exception-demo') || n.demoEndpoint.includes('/error')) return 'error';
  if (n.demoEndpoint.includes('/file')) return 'file';
  if (n.demoEndpoint.includes('/util/')) return 'utility';
  if (n.demoEndpoint.includes('/crypto/')) return 'crypto';
  if (n.demoEndpoint.includes('/jpa/')) return 'jpa';
  if (n.demoEndpoint.includes('/cache')) return 'cache';
  if (n.demoEndpoint.includes('/demo/kafka')) return 'kafka';
  if (n.demoEndpoint.includes('/demo/rabbitmq')) return 'rabbitmq';
  if (n.demoEndpoint.includes('/demo/elasticsearch')) return 'elasticsearch';
  if (n.demoEndpoint.includes('/demo/mongo')) return 'mongo';
  if (n.demoEndpoint.includes('/demo/otel')) return 'otel';
  if (n.demoEndpoint.includes('/demo/outbox')) return 'outbox';
  if (n.demoEndpoint.includes('/demo/oauth2')) return 'oauth2';
  // queue #646 §R646-4: 미데모 10종 신규 데모 패널
  if (n.demoEndpoint.includes('/demo/state-machine')) return 'stateMachine';
  if (n.demoEndpoint.includes('/demo/saga')) return 'saga';
  if (n.demoEndpoint.includes('/demo/report')) return 'report';
  if (n.demoEndpoint.includes('/demo/retention')) return 'retention';
  if (n.demoEndpoint.includes('/demo/config')) return 'config';
  if (n.demoEndpoint.includes('/demo/barcode')) return 'barcode';
  if (n.demoEndpoint.includes('/demo/template')) return 'template';
  if (n.demoEndpoint.includes('/demo/pdf')) return 'pdf';
  if (n.demoEndpoint.includes('/demo/image')) return 'image';
  if (n.demoEndpoint.includes('/demo/feature-toggle')) return 'featureToggle';
  return 'info';
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function CommonShowcasePage() {
  const { t } = useTranslation();
  const location = useLocation();
  // queue #663 §UAT-2026-07-09-R663 §R663-3: 피드백 관리 링크 — ROLE_ADMIN 만 노출(권한 분기)
  const isFeedbackAdmin = useHasRole('ROLE_ADMIN');
  // queue #640 §R640-3: 필터(패싯 5종 + 검색어 q)의 단일 진실 = URL. 로컬 state 이중화 금지 —
  // searchQuery 는 더 이상 useState 가 아니라 searchParams.get('q') 파생값이다.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') ?? '';
  function setSearchQuery(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }
  const [selectedNode, setSelectedNode] = useState<ModuleNode | null>(null);
  const [activePanel, setActivePanel] = useState<DemoPanel | null>(null);
  // queue #664 §UAT-2026-07-09-R664 §R664-3: §R658 의 showRecentChanges boolean 을 3분기
  // activeView 로 승격(카탈로그|레시피|최근 변경) · `?view=` URL 동기화(§R640 URL-as-SSOT 계보).
  // queue #666 §UAT-2026-07-09-R666 §R666-4: 3분기 → 4분기(+함정 사전). 신규 라우트 불요
  // — 동일 /showcase.
  // queue #667 §UAT-2026-07-09-R667 §R667-4: 4분기 → 5분기(+선택 가이드). 시각 순서는 채택
  // 여정(브라우징→선택→조합→변경→트러블슈팅)에 맞춰 카탈로그 다음(2번째)에 배치.
  const viewParam = searchParams.get('view');
  const activeView: 'catalog' | 'decision' | 'recipes' | 'recent' | 'pitfalls' =
    viewParam === 'decision'
      ? 'decision'
      : viewParam === 'recipes'
      ? 'recipes'
      : viewParam === 'recent'
      ? 'recent'
      : viewParam === 'pitfalls'
      ? 'pitfalls'
      : 'catalog';
  function setActiveView(view: 'catalog' | 'decision' | 'recipes' | 'recent' | 'pitfalls') {
    const next = new URLSearchParams(searchParams);
    if (view === 'catalog') next.delete('view');
    else next.set('view', view);
    if (view !== 'decision') {
      next.delete('guide');
      next.delete('highlight');
    }
    if (view !== 'recipes') next.delete('recipe');
    if (view !== 'pitfalls') {
      next.delete('pitfall');
      next.delete('sev');
      next.delete('code');
    }
    setSearchParams(next, { replace: true });
  }
  // queue #664 §R664-3: 레시피 상세 딥링크 — `?recipe=<id>`(레시피 뷰에서만 유효)
  const selectedRecipeId = searchParams.get('recipe');
  const selectedRecipe = selectedRecipeId ? RECIPES.find((r) => r.id === selectedRecipeId) ?? null : null;
  function handleSelectRecipe(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'recipes');
    next.set('recipe', id);
    setSearchParams(next, { replace: true });
  }
  function handleBackToRecipeGallery() {
    const next = new URLSearchParams(searchParams);
    next.delete('recipe');
    setSearchParams(next, { replace: true });
  }
  // queue #666 §UAT-2026-07-09-R666 §R666-4: 함정 사전 상세 딥링크 — `?pitfall=<id>`(함정
  // 사전 뷰에서만 유효). 카드 그리드/필터는 `PitfallDictionary` 가 자체 `q`/`sev`/`code`
  // 쿼리를 관리한다(§R640 URL-as-SSOT 계보).
  const selectedPitfallId = searchParams.get('pitfall');
  const selectedPitfall = selectedPitfallId ? PITFALLS.find((p) => p.id === selectedPitfallId) ?? null : null;
  function handleSelectPitfall(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'pitfalls');
    next.set('pitfall', id);
    setSearchParams(next, { replace: true });
  }
  function handleBackToPitfallDictionary() {
    const next = new URLSearchParams(searchParams);
    next.delete('pitfall');
    setSearchParams(next, { replace: true });
  }
  // queue #666 §R666-4: 함정 상세의 관련 모듈 배지 / 트러블슈팅 카드 클릭 — 카탈로그 뷰로
  // 전환 + 해당 노드 선택(jumpToCode 재사용 · §R664 handleSelectModuleFromRecipe 동형).
  function handleSelectModuleFromPitfall(code: string) {
    setActiveView('catalog');
    jumpToCode(code);
  }
  // queue #667 §UAT-2026-07-09-R667 §R667-4: 선택 가이드 상세 딥링크 — `?guide=<id>`(선택
  // 가이드 뷰에서만 유효) + (선택) `?highlight=<code>`(모듈 상세 '대안과 비교' 진입점 딥링크).
  const selectedGuideId = searchParams.get('guide');
  const highlightCode = searchParams.get('highlight');
  function handleSelectDecisionGuide(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'decision');
    next.set('guide', id);
    next.delete('highlight');
    setSearchParams(next, { replace: true });
  }
  // queue #667 §R667-3: 모듈 상세 '대안과 비교' 카드 클릭 — 전역 선택 가이드 뷰로 전환 +
  // 해당 군 선택 + 현재 코드 하이라이트(§R667-3 highlight 딥링크와 정합).
  function handleNavigateToAlternatives(guideId: string, code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'decision');
    next.set('guide', guideId);
    next.set('highlight', code);
    setSearchParams(next, { replace: true });
  }
  // queue #667 §R667-4: 선택 가이드 비교표의 후보 코드 클릭 — 카탈로그 뷰로 전환 + 해당
  // 노드 선택(jumpToCode 재사용 · §R664/§R666 handleSelectModuleFrom* 동형).
  function handleSelectModuleFromDecision(code: string) {
    setActiveView('catalog');
    jumpToCode(code);
  }
  // queue #691 §UAT-2026-07-14-R691 §R691-2(d): 헤더 🔔 배지 진입점 — `?feedback=mine` 이면
  // 페이지-레벨 FeedbackDialog(moduleCode=null·initialTab='mine')를 렌더한다. 기존 모듈 상세
  // 내부 인스턴스(ModuleDetailBody)는 무변경. URL=단일 진실(닫으면 파라미터 제거).
  const feedbackMineOpen = searchParams.get('feedback') === 'mine';
  function handleCloseFeedbackMine() {
    const next = new URLSearchParams(searchParams);
    next.delete('feedback');
    setSearchParams(next, { replace: true });
  }
  const [feedbackMineToast, setFeedbackMineToast] = useState<{ text: string; tone: 'success' | 'error' } | null>(
    null
  );
  const showFeedbackMineToast = (text: string, tone: 'success' | 'error') => {
    setFeedbackMineToast({ text, tone });
    setTimeout(() => setFeedbackMineToast(null), 4000);
  };
  // queue #693 §UAT-2026-07-14-R693 §R693-5: 통합검색 'spec' 그룹 선택 →
  // `/showcase?spec=<slug>#<CODE>` — 해시(#679)가 노드를 선택하고, `?spec=` 은 선택된 노드의
  // ModuleDocsModal 을 자동 오픈 + 앵커 스크롤한다. 모달이 닫히면 상위가 파라미터를 제거한다.
  const specSlugParam = searchParams.get('spec');
  function handleSpecModalAutoOpenConsumed() {
    const next = new URLSearchParams(searchParams);
    next.delete('spec');
    setSearchParams(next, { replace: true });
  }
  // ★일자 시한폭탄 금지 — now 는 페이지 마운트 시 1회만 계산해 하위(패널/트리)에 주입
  const now = useMemo(() => new Date(), []);

  const { data, isLoading, isError, error, refetch } = useModuleCatalog();

  // queue #677 §UAT-2026-07-13-R677 §R677-4②: 구독 코드 집합 — '구독 모듈만' 패싯 판정에
  // 사용(useWatches 로딩/실패 중엔 빈 Set 으로 방어 — matchWatch 는 watch!=='1' 이면 항상
  // true 이므로 필터 미적용 시엔 영향 없음).
  const { data: watchesData } = useWatches();
  const watchedCodesSet = useMemo(() => new Set(watchesData?.moduleCodes ?? []), [watchesData]);

  // queue #640 §R640-4: 5 패싯 + 문자열 검색을 AND 로 합성한 단일 selector. 트리는
  // 이미 필터된 목록을 받아 pass-through 로만 렌더(count 단일 진실 — 이중 필터 발산 방지).
  // queue #677 §R677-4②: 'watch' 패싯(구독 모듈만) 을 6번째 AND 조건으로 추가.
  const visibleNodes = useMemo(() => {
    if (!data) return [];
    const category = searchParams.get('category');
    const status = searchParams.get('status');
    const demo = searchParams.get('demo');
    const optin = searchParams.get('optin');
    const biz = searchParams.get('biz');
    const watch = searchParams.get('watch');
    const q = searchParams.get('q');
    return data.nodes.filter(
      (n) =>
        matchCategory(n, category) &&
        matchStatus(n, status) &&
        matchDemo(n, demo) &&
        matchOptin(n, optin, TAKE_THIS_DATA) &&
        matchBiz(n, biz) &&
        matchWatch(n, watch, watchedCodesSet) &&
        matchQuery(n, q)
    );
  }, [data, searchParams, watchedCodesSet]);

  // queue #677 §R677-4②: watch=1 로 인해 트리가 비었을 때만 전용 EmptyState 문구('구독한
  // 모듈이 없습니다')를 보여준다 — 다른 패싯 조합으로 인한 빈 결과는 기존 일반 문구 유지.
  const isWatchEmptyState = searchParams.get('watch') === '1' && watchedCodesSet.size === 0;

  function resetAllFilters() {
    const next = new URLSearchParams(searchParams);
    ['category', 'status', 'demo', 'optin', 'biz', 'watch', 'q'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  }

  /**
   * #336 — Auto-select a node when navigating from CommonFeaturesTab demo button.
   * Reads router state.scrollTo (e.g. "C003") or window.location.hash (e.g. "#G004").
   *
   * queue #665 §UAT-2026-07-09-R665 §R665-5: 통합검색 '설정 속성' hit 는 코드가 아니라
   * `#config-<name>` 해시 + `?artifact=<a>` 쿼리로 도달한다 — 해시가 코드 매칭에 실패하는
   * config 앵커인 경우에만 artifact 로 노드를 선택한다(일반 코드 해시 동작은 무변경).
   */
  const artifactParam = searchParams.get('artifact');
  useEffect(() => {
    if (!data || data.nodes.length === 0) return;

    // Router state takes priority; fall back to URL hash
    const stateCode = (location.state as { scrollTo?: string } | null)?.scrollTo;
    const hashCode = window.location.hash ? window.location.hash.replace('#', '') : null;
    const isConfigAnchor = hashCode?.startsWith('config-') ?? false;
    const targetCode = stateCode ?? (isConfigAnchor ? null : hashCode);

    let node = targetCode ? data.nodes.find((n) => n.code === targetCode) : undefined;
    if (!node && isConfigAnchor && artifactParam) {
      node = data.nodes.find((n) => n.artifact === artifactParam);
    }
    // queue #679 §R679-4 가드 B: 이미 선택된 노드에 대한 재선택은 스킵(멱등) — select→hash→
    // (본 effect)→동일 노드 재set 로 인한 렌더 재귀를 방어(회귀 방어 · 현 구조는 replaceState 가
    // popstate 미발화라 자연적으로는 재발화하지 않음).
    if (node && node.code === selectedNode?.code) return;
    if (node) {
      setSelectedNode(node);
      setActivePanel(resolveDemoPanel(node));
    }
  // Only run when data arrives or location/artifact changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, location.state, location.hash, artifactParam]);

  // queue #665 §R665-5: config 앵커 딥링크 도달 시, 노드 선택(위 effect) 이후 '설정' 섹션의
  // 해당 속성 행으로 스크롤한다(§R659 dev-standards StandardsSearchBar 의 getElementById +
  // scrollIntoView 패턴 재사용).
  useEffect(() => {
    if (!selectedNode) return;
    const hashCode = window.location.hash ? window.location.hash.replace('#', '') : null;
    if (!hashCode || !hashCode.startsWith('config-')) return;
    const timerId = window.setTimeout(() => {
      document.getElementById(hashCode)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => window.clearTimeout(timerId);
  }, [selectedNode, location.hash]);

  function handleNodeSelect(node: ModuleNode, panel: DemoPanel) {
    setSelectedNode(node);
    setActivePanel(panel);
    // queue #679 §R679-2: 아웃바운드 해시 동기화 — 노드 확정 직후 write.
    syncNodeHash(node.code);
  }

  // #617 — SUPERSEDED 노드 상세의 "후계 코드로 이동" 버튼. 트리 클릭과 동일하게 노드
  // 선택 + 패널 재계산만 수행 — 라우트 이동 없음(같은 페이지 내 노드 전환).
  function jumpToCode(code: string) {
    const target = data?.nodes.find((n) => n.code === code);
    if (!target) return;
    setSelectedNode(target);
    setActivePanel(resolveDemoPanel(target));
    // queue #679 §R679-2: 아웃바운드 해시 동기화 — 노드 확정 직후 write.
    syncNodeHash(target.code);
  }

  // queue #664 §R664-3: 레시피 상세의 구성 모듈 배지 / '/showcase#<code>' 관련 데모 클릭 —
  // 카탈로그 뷰로 전환 + 해당 노드 선택(jumpToCode 재사용).
  function handleSelectModuleFromRecipe(code: string) {
    setActiveView('catalog');
    jumpToCode(code);
  }

  function renderDemoPanel() {
    if (!selectedNode) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 240,
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
            textAlign: 'center',
            padding: 'var(--spacing-8)',
          }}
        >
          <span
            aria-hidden="true"
            style={{ fontSize: 40, marginBottom: 'var(--spacing-4)', display: 'block' }}
          >
            ←
          </span>
          <p style={{ margin: 0 }}>{t('showcase.panel.placeholder')}</p>
        </div>
      );
    }

    // Panel code → hint key mapping for demo panels
    const panelToHintKey: Partial<Record<DemoPanel, string>> = {
      validate: 'c003',
      mask: 'c013',
      excel: 'c078',
      event: 'c008',
      error: 'c001',
      file: 'c009',
      cache: 'cache',
      utility: 'c004',
      crypto: 'c012',
      jpa: 'c039',
      kafka: 'c055',
      rabbitmq: 'c057',
      elasticsearch: 'c053',
      mongo: 'c054',
      otel: 'c072',
      outbox: 'c056',
      oauth2: 'c035',
    };

    const hintKey = activePanel
      ? (panelToHintKey[activePanel] ?? toHintKey(selectedNode.code))
      : toHintKey(selectedNode.code);

    // queue #679 §UAT-2026-07-13-R679 §R679-3: 상세 패널 헤더 '링크 복사' 스트립 — info/데모
    // 공통 진입점(renderDemoPanel 최상단)이라 양쪽 노드 유형 전체에 노출된다. 복사 대상 =
    // 해시 포함 절대 URL(§R679-2 로 해시가 선택 노드와 이미 동기화된 뒤이므로 window.location.href
    // 가 곧 "지금 보는 노드"의 퍼머링크). clipboard 는 @ui-kit CopyButton 재사용(재구현 금지).
    // queue #677 §UAT-2026-07-13-R677 §R677-4①: WatchToggle 을 §R679 CopyButton 과 같은
    // 헤더 스트립에 나란히 배치(공존) — 노드 유형(info/데모) 공통 진입점.
    const copyLinkStrip = (
      <div
        data-testid="showcase-copy-link"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <WatchToggle moduleCode={selectedNode.code} />
        <CopyButton
          text={window.location.href}
          ariaLabel={t('showcase.page.copyLinkAria', { code: selectedNode.code })}
          copyLabel={t('showcase.page.copyLink')}
          copiedLabel={t('showcase.page.copyLinkDone')}
        />
      </div>
    );

    const demoContent = (() => {
      switch (activePanel) {
        case 'validate': return <ValidatePanel />;
        case 'mask':     return <MaskPanel />;
        case 'excel':    return <ExcelPanel />;
        case 'event':    return <EventPanel />;
        case 'error':    return <ErrorPanel />;
        case 'file':     return <FilePanel />;
        case 'cache':    return <CachePanel />;
        case 'utility':  return <UtilityPanel />;
        case 'crypto':   return <CryptoPanel />;
        case 'jpa':         return <JpaPanel />;
        case 'routeMatch':  return <RouteMatchPanel />;
        case 'authCheck':   return <AuthCheckPanel />;
        case 'rateLimit':   return <RateLimitPanel />;
        case 'healthCheck': return <HealthCheckPanel />;
        case 'kafka':       return <KafkaDemoPanel />;
        case 'rabbitmq':    return <RabbitDemoPanel />;
        case 'elasticsearch': return <ElasticsearchDemoPanel />;
        case 'mongo':       return <MongoDemoPanel />;
        case 'otel':        return <OtelStatusPanel />;
        case 'outbox':      return <OutboxDemoPanel />;
        case 'oauth2':      return <OAuth2DemoPanel />;
        // queue #646 §R646-4: 미데모 10종 신규 데모 패널
        case 'stateMachine':  return <StateMachineDemoPanel />;
        case 'saga':          return <SagaDemoPanel />;
        case 'report':        return <ReportDemoPanel />;
        case 'retention':     return <RetentionPreviewDemoPanel />;
        case 'config':        return <ConfigLookupDemoPanel />;
        case 'barcode':       return <BarcodeDemoPanel />;
        case 'template':      return <TemplateRenderDemoPanel />;
        case 'pdf':           return <PdfDemoPanel />;
        case 'image':         return <ImageResizeDemoPanel />;
        case 'featureToggle': return <FeatureToggleDemoPanel />;
        case 'info':
        default:
          return (
            <InfoPanel
              node={selectedNode}
              onJumpToCode={jumpToCode}
              onSelectPitfall={handleSelectPitfall}
              onNavigateToAlternatives={handleNavigateToAlternatives}
              autoOpenSpecSlug={specSlugParam}
              onAutoOpenSpecConsumed={handleSpecModalAutoOpenConsumed}
            />
          );
      }
    })();

    // InfoPanel already renders LearningHints internally; skip wrapping for 'info'
    if (activePanel === 'info' || !activePanel) {
      return (
        <>
          {copyLinkStrip}
          {demoContent}
        </>
      );
    }

    return (
      <>
        {copyLinkStrip}
        {demoContent}

        {/* UAT 2026-06-01 #466: 데모 아래에 비-데모 항목과 동일한 상세 안내 구조 노출 */}
        <section
          aria-label={t('showcase.panel.detailsAria')}
          style={{
            marginTop: 'var(--spacing-6)',
            borderTop: '1px solid var(--color-surface-border)',
            paddingTop: 'var(--spacing-4)',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: '0 0 var(--spacing-3) 0',
            }}
          >
            {t('showcase.panel.detailsTitle')}
          </h3>
          <ModuleDetailBody
            node={selectedNode}
            onJumpToCode={jumpToCode}
            onSelectPitfall={handleSelectPitfall}
            onNavigateToAlternatives={handleNavigateToAlternatives}
            autoOpenSpecSlug={specSlugParam}
            onAutoOpenSpecConsumed={handleSpecModalAutoOpenConsumed}
          />
        </section>

        {/* R7-021: 3 learning hint cards appended to every demo panel */}
        <LearningHints moduleKey={hintKey} />
      </>
    );
  }

  return (
    <>
      <style>{KEYFRAME_CSS}</style>

      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: 'var(--spacing-6)',
        }}
      >
        {/* Page header */}
        <PageHeader
          title={t('showcase.page.title')}
          breadcrumb={
            <span>
              {t('showcase.page.title').split(' ')[0]} /{' '}
              <span style={{ color: 'var(--color-text-primary)' }}>{t('showcase.page.title')}</span>
            </span>
          }
          actions={
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              {/* queue #663 §R663-3: 피드백 관리 — ROLE_ADMIN 만 노출(비-admin 은 미노출) */}
              {isFeedbackAdmin && (
                <Link
                  to="/showcase/feedback"
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-brand-primary)',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  {t('showcase.feedback.admin.navLink')}
                </Link>
              )}
              {/* queue #687 §R687-3(a): 채택 현황 관리 — ROLE_ADMIN 만 노출(비-admin 은 미노출) */}
              {isFeedbackAdmin && (
                <Link
                  to="/showcase/adoption"
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-brand-primary)',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  {t('showcase.adoption.link')}
                </Link>
              )}
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t('showcase.page.subtitle')}
              </span>
            </div>
          }
        />

        {/* R11-#286: Permanent guide paragraph — replaces the old InfoBanner */}
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.7,
            marginBottom: 'var(--spacing-4)',
            marginTop: 0,
          }}
        >
          {t('showcase.guide.intro')}
        </p>

        {/* User info strip */}
        {data && data.currentUserPermissions.length > 0 && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            {t('showcase.page.currentRoles')} {data.currentUserPermissions.join(', ')}
          </div>
        )}

        {/* queue #664 §UAT-2026-07-09-R664 §R664-3: 뷰 세그먼트(카탈로그 | 레시피 | 최근 변경) —
            §R658 showRecentChanges 토글을 3분기로 승격 · `?view=` URL 동기화(§R640 계보 답습).
            queue #666 §UAT-2026-07-09-R666 §R666-4: 3분기 → 4분기(+함정 사전).
            queue #667 §UAT-2026-07-09-R667 §R667-4: 4분기 → 5분기(+선택 가이드 · 카탈로그
            다음(2번째) 배치 — 채택 여정: 브라우징→선택→조합→변경→트러블슈팅).
            신규 라우트 불요(동일 /showcase). */}
        <div
          role="group"
          aria-label={t('showcase.view.switcherAria')}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--spacing-2)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {(['catalog', 'decision', 'recipes', 'recent', 'pitfalls'] as const).map((view) => (
            <button
              key={view}
              type="button"
              aria-pressed={activeView === view}
              onClick={() => setActiveView(view)}
              style={{
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                background: activeView === view ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                color: activeView === view ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {view === 'catalog'
                ? t('showcase.view.catalogTab')
                : view === 'decision'
                ? t('showcase.decision.view.tab')
                : view === 'recipes'
                ? t('showcase.recipe.view.tab')
                : view === 'recent'
                ? t('showcase.recentChanges.toggle')
                : t('showcase.pitfall.view.tab')}
            </button>
          ))}
        </div>

        {/* '최근 변경' 뷰 — 전역 변경 피드(§R658-2 · 전체 폭, 카탈로그/데모 분할 레이아웃 대체) */}
        {activeView === 'recent' && data && (
          <RecentChangesPanel
            maturity={MODULE_MATURITY}
            nodes={data.nodes}
            onClose={() => setActiveView('catalog')}
          />
        )}

        {/* '선택 가이드' 뷰 — 6개 비교군 카드 + 선택된 군 비교표(§R667-4) */}
        {activeView === 'decision' && (
          <DecisionGuideView
            guides={DECISION_GUIDES}
            nodes={data?.nodes ?? []}
            selectedGuideId={selectedGuideId}
            highlightCode={highlightCode}
            onSelectGuide={handleSelectDecisionGuide}
            onSelectModule={handleSelectModuleFromDecision}
            onSelectRecipe={handleSelectRecipe}
            onSelectPitfall={handleSelectPitfall}
          />
        )}

        {/* '레시피' 뷰 — 갤러리(기본) 또는 `?recipe=<id>` 선택된 레시피 상세(§R664-3) */}
        {activeView === 'recipes' && (
          selectedRecipe ? (
            <RecipeDetailPanel
              recipe={selectedRecipe}
              onBack={handleBackToRecipeGallery}
              onSelectModule={handleSelectModuleFromRecipe}
            />
          ) : (
            <RecipeGallery recipes={RECIPES} onSelect={handleSelectRecipe} />
          )
        )}

        {/* '함정 사전' 뷰 — 카드 그리드(기본) 또는 `?pitfall=<id>` 선택된 함정 상세(§R666-4) */}
        {activeView === 'pitfalls' && (
          selectedPitfall ? (
            <PitfallDetailPanel
              pitfall={selectedPitfall}
              onBack={handleBackToPitfallDictionary}
              onSelectModule={handleSelectModuleFromPitfall}
            />
          ) : (
            <PitfallDictionary pitfalls={PITFALLS} onSelect={handleSelectPitfall} />
          )
        )}

        {/* '카탈로그' 뷰(기본) — Split layout */}
        {activeView === 'catalog' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'clamp(240px, 280px, 320px) 1fr',
            gap: 'var(--spacing-4)',
            alignItems: 'start',
          }}
        >
          {/* Left: Catalog tree */}
          <aside
            aria-label={t('showcase.catalog.ariaLabel')}
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: 'var(--color-surface-background)',
            }}
          >
            {/* Search */}
            <div
              style={{
                padding: 'var(--spacing-3)',
                borderBottom: '1px solid var(--color-surface-border)',
                background: 'var(--color-surface-elevated)',
              }}
            >
              <input
                type="search"
                placeholder={t('showcase.catalog.search')}
                aria-label={t('showcase.catalog.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family-sans)',
                  background: 'var(--color-surface-background)',
                  color: 'var(--color-text-primary)',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Tree content */}
            {isLoading && <CatalogSkeleton />}

            {isError && (
              <div style={{ padding: 'var(--spacing-4)' }}>
                <ErrorState
                  title={t('showcase.catalog.loadFailed')}
                  message={error?.message ?? t('showcase.error.networkError')}
                  onRetry={() => { void refetch(); }}
                />
              </div>
            )}

            {!isLoading && !isError && data && data.nodes.length === 0 && (
              <div style={{ padding: 'var(--spacing-4)' }}>
                <EmptyState
                  title={t('showcase.catalog.empty')}
                  description={t('showcase.catalog.emptyDesc')}
                  action={
                    <button
                      type="button"
                      onClick={() => refetch()}
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-4)',
                        background: 'var(--color-brand-primary)',
                        color: 'var(--color-text-inverse)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontFamily: 'var(--font-family-sans)',
                      }}
                    >
                      {t('showcase.catalog.reload')}
                    </button>
                  }
                />
              </div>
            )}

            {/* queue #640 §R640-1: 데이터 로딩 전엔 필터 바 숨김(값 원천 = data.nodes/take-this.json) */}
            {!isLoading && !isError && data && data.nodes.length > 0 && (
              <>
                <ModuleFacetBar
                  nodes={data.nodes}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  visibleCount={visibleNodes.length}
                  totalCount={data.nodes.length}
                />

                {visibleNodes.length === 0 ? (
                  <div style={{ padding: 'var(--spacing-4)' }}>
                    <EmptyState
                      title={isWatchEmptyState ? t('showcase.tree.watchEmpty') : t('showcase.filter.emptyTitle')}
                      action={
                        <button
                          type="button"
                          onClick={resetAllFilters}
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-4)',
                            background: 'var(--color-brand-primary)',
                            color: 'var(--color-text-inverse)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)',
                            fontFamily: 'var(--font-family-sans)',
                          }}
                        >
                          {t('showcase.filter.reset')}
                        </button>
                      }
                    />
                  </div>
                ) : (
                  <ModuleCatalogTree
                    nodes={visibleNodes}
                    selectedCode={selectedNode?.code ?? null}
                    onSelect={handleNodeSelect}
                    now={now}
                  />
                )}
              </>
            )}
          </aside>

          {/* Right: Demo panel */}
          <main
            role="main"
            aria-label={t('showcase.panel.demoLabel')}
            style={{
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-6)',
              background: 'var(--color-surface-background)',
              minHeight: 480,
            }}
          >
            {renderDemoPanel()}
          </main>
        </div>
        )}

        {/* Mobile: stack vertically on small screens */}
        <style>{`
          @media (max-width: 768px) {
            [aria-label="${t('showcase.catalog.ariaLabel')}"],
            [role="main"][aria-label="${t('showcase.panel.demoLabel')}"] {
              grid-column: 1 / -1;
            }
          }
        `}</style>

        {/* H: biz cross-link InfoCards */}
        <BizCrossLinkCards />

        {/* Common modules panel */}
        <CommonUsagePanel />

        {/* queue #691 §R691-0③: 페이지-레벨 '내 문의 이력' 진입점 — 헤더 🔔 배지 클릭 시
            `?feedback=mine` 로 도달. moduleCode=null(일반 문의 진입 · 모듈 상세 인스턴스와 분리). */}
        <FeedbackDialog
          open={feedbackMineOpen}
          moduleCode={null}
          initialTab="mine"
          onClose={handleCloseFeedbackMine}
          onSuccess={(msg) => showFeedbackMineToast(msg, 'success')}
          onError={(msg) => showFeedbackMineToast(msg, 'error')}
        />

        {feedbackMineToast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              bottom: 'var(--spacing-6)',
              right: 'var(--spacing-6)',
              zIndex: 1100,
              padding: 'var(--spacing-3) var(--spacing-5)',
              borderRadius: 'var(--radius-md)',
              background: feedbackMineToast.tone === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              fontSize: 'var(--font-size-sm)',
              boxShadow: 'var(--shadow-lg)',
              maxWidth: 360,
            }}
          >
            {feedbackMineToast.text}
          </div>
        )}
      </div>
    </>
  );
}
